import styles from './index.module.scss';
import { Carousel } from 'antd';
import type { CarouselRef } from 'antd/es/carousel';
import React, { useEffect, useRef, useState } from 'react';
import { DateType, momTitleFunc, formatNumber } from '../utils';
import { keepTwoNull, keepPercentOrLine } from '@/utils/utils';
import { EnergyMonitorContext } from '../';
import { isNil } from 'lodash-es';
import { StateEnergyProps } from '../MakeUseOf';
import {
  apiV2HighEnergyDeviceDurationAndAttributeDataPost,
  V2HighEnergyDeviceDurationAndAttributeDataPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import useLatestRequest from '../useLatestRequest';
type CarouselProps = V2HighEnergyDeviceDurationAndAttributeDataPostResponse;
interface TimeCarouselProps {
  timeResolution?: string;
  dataQueryPeriodValue?: string;
  timeRangeTidy?: number[];
  dataQueryPeriodId?: number;
  stateChangeData?: StateEnergyProps;
}
const TimeCarousel: React.FC<TimeCarouselProps> = ({
  timeResolution,
  timeRangeTidy,
  dataQueryPeriodId,
  dataQueryPeriodValue,
  stateChangeData,
}) => {
  const { deviceLedgerId } = React.useContext(EnergyMonitorContext);
  const carouselRef = useRef<CarouselRef>(null);
  const [isGranularity, setIsGranularity] = useState<boolean>(false);
  const [isPeriodId, setIsPeriodId] = useState<boolean>(!!dataQueryPeriodId);
  const [carousel, setCarousel] = useState<CarouselProps>();
  const [periodDeal, setPeriodDeal] = useState<CarouselProps['periodData']>();
  const [totalDeal, setTotalDeal] = useState<CarouselProps['totalData']>();
  const { runLatest, cancelLatest } = useLatestRequest();

  useEffect(() => {
    if (timeResolution === DateType.WEEK || timeResolution === DateType.MONTH) {
      setIsGranularity(true);
    } else {
      setIsGranularity(false);
    }
  }, [timeResolution]);

  useEffect(() => {
    if (!!dataQueryPeriodId) {
      setIsPeriodId(true);
    } else {
      setIsPeriodId(false);
    }
  }, [dataQueryPeriodId]);

  useEffect(() => {
    if (!deviceLedgerId || !timeRangeTidy) {
      cancelLatest();
      setCarousel(undefined);
      setPeriodDeal(undefined);
      setTotalDeal(undefined);
      return;
    }

    runLatest(
      () =>
        apiV2HighEnergyDeviceDurationAndAttributeDataPost({
          deviceLedgerId,
          startTime: timeRangeTidy[0],
          endTime: timeRangeTidy[1],
          timeResolution,
          dataQueryPeriodId,
        }),
      {
        onSuccess: res => {
          setCarousel(res);
          setPeriodDeal(res.periodData ?? {});
          setTotalDeal(res.totalData ?? {});
        },
      }
    );
  }, [cancelLatest, dataQueryPeriodId, deviceLedgerId, runLatest, timeRangeTidy, timeResolution]);

  function renderTrendIndicator(periodDeal?: number) {
    if (isGranularity) {
      return (
        <div>
          <span>同比去年：</span>
          {TrendIndicator(periodDeal)}
        </div>
      );
    }
    return null;
  }

  function renderQueryPeriodId(
    label: string,
    TimeTrend?: number,
    periodDeal?: number,
    TimeRatio?: number,
    TimePercent?: number,
    TimeCost?: number,
    energy?: number
  ) {
    if (isPeriodId) {
      return (
        <div className={styles.row}>
          <div className={styles.trend}>
            <div>
              {dataQueryPeriodValue}
              {label}时长
            </div>
            {renderTrendIndicator(TimeTrend)}
          </div>
          <div className={styles.trend}>
            <div className={styles.value}>
              {periodDeal ?? '--'}h
              <span className={styles.proportion}>{TimePercent ? `(${(TimePercent * 100).toFixed(1)}%)` : '(--)'}</span>
            </div>
            <div>
              <span>{momTitleFunc(timeResolution as DateType)}：</span>
              {TrendIndicator(TimeRatio)}
            </div>
          </div>
          <div className={styles.trend}>
            <div>
              用能：
              <span className={styles.energy}>
                {keepTwoNull(energy)}
                {stateChangeData?.generalUnitName}
              </span>
            </div>
          </div>
          <div className={styles.trend}>
            <div>
              用能成本：
              <span className={styles.energy}>{keepTwoNull(TimeCost)}元</span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  }

  const getPeriodStyle = () => {
    if (isPeriodId) {
      return { paddingBottom: 8, borderBottom: '1px dashed var(--mx-tabs-base-border-color)' };
    }
  };
  return (
    <div className={styles.durationData}>
      <div className={styles.item}>
        <div className={styles.row} style={getPeriodStyle()}>
          <div className={styles.trend}>
            <div>运行时长</div>
            {renderTrendIndicator(totalDeal?.runningTimeTrend)}
          </div>

          <div className={styles.trend}>
            <div className={styles.value}>
              {totalDeal?.runningTime ?? '--'}h
              <span className={styles.proportion}>
                {totalDeal?.runningTimePercent ? `(${(totalDeal?.runningTimePercent * 100).toFixed(1)}%)` : '(--)'}
              </span>
            </div>
            <div>
              <span>{momTitleFunc(timeResolution as DateType)}：</span>
              {TrendIndicator(totalDeal?.runningTimeRatio)}
            </div>
          </div>
          <div className={styles.trend}>
            <div>
              用能：
              <span className={styles.energy}>
                {keepTwoNull(stateChangeData?.totalData?.runningEnergy?.totalValue)}
                {stateChangeData?.generalUnitName}
              </span>
            </div>
          </div>
          <div className={styles.trend}>
            <div>
              用能成本：
              <span className={styles.energy}>{keepTwoNull(totalDeal?.runningTimeCost)}元</span>
            </div>
          </div>
        </div>
        {renderQueryPeriodId(
          '运行',
          periodDeal?.runningTimeTrend,
          periodDeal?.runningTime,
          periodDeal?.runningTimeRatio,
          periodDeal?.runningTimePercent,
          periodDeal?.runningTimeCost,
          stateChangeData?.periodData?.runningEnergy?.totalValue
        )}
      </div>

      <div className={styles.item}>
        <div className={styles.row} style={getPeriodStyle()}>
          <div className={styles.trend}>
            <div>待机时长</div>
            {renderTrendIndicator(totalDeal?.standbyTimeTrend)}
          </div>
          <div className={styles.trend}>
            <div className={styles.value}>
              {totalDeal?.standbyTime ?? '--'}h
              <span className={styles.proportion}>
                {totalDeal?.standbyTimePercent ? `(${(totalDeal?.standbyTimePercent * 100).toFixed(1)}%)` : '(--)'}
              </span>
            </div>
            <div>
              <span>{momTitleFunc(timeResolution as DateType)}：</span>
              {TrendIndicator(totalDeal?.standbyTimeRatio)}
            </div>
          </div>
          <div className={styles.trend}>
            <div>
              用能：
              <span className={styles.energy}>
                {keepTwoNull(stateChangeData?.totalData?.standbyEnergy?.totalValue)}
                {stateChangeData?.generalUnitName}
              </span>
            </div>
          </div>
          <div className={styles.trend}>
            <div>
              用能成本：
              <span className={styles.energy}>{keepTwoNull(totalDeal?.standbyTimeCost)}元</span>
            </div>
          </div>
        </div>
        {renderQueryPeriodId(
          '待机',
          periodDeal?.standbyTimeTrend,
          periodDeal?.standbyTime,
          periodDeal?.standbyTimeRatio,
          periodDeal?.standbyTimePercent,
          periodDeal?.standbyTimeCost,
          stateChangeData?.periodData?.standbyEnergy?.totalValue
        )}
      </div>

      <div className={styles.item}>
        <div className={styles.row} style={getPeriodStyle()}>
          <div className={styles.trend}>
            <div>停机时长</div>
            {renderTrendIndicator(totalDeal?.shutdownTimeTrend)}
          </div>

          <div className={styles.trend}>
            <div className={styles.value}>
              {totalDeal?.shutdownTime ?? '--'}h
              <span className={styles.proportion}>
                {totalDeal?.shutdownTimePercent ? `(${(totalDeal?.shutdownTimePercent * 100).toFixed(1)}%)` : '(--)'}
              </span>
            </div>
            <div>
              <span>{momTitleFunc(timeResolution as DateType)}：</span>
              {TrendIndicator(totalDeal?.shutdownTimeRatio)}
            </div>
          </div>
          <div className={styles.trend}>
            <div>
              用能：
              <span className={styles.energy}>
                {keepTwoNull(stateChangeData?.totalData?.shutdownEnergy?.totalValue)}
                {stateChangeData?.generalUnitName}
              </span>
            </div>
          </div>
          <div className={styles.trend}>
            <div>
              用能成本：
              <span className={styles.energy}>{keepTwoNull(totalDeal?.shutdownTimeCost)}元</span>
            </div>
          </div>
        </div>
        {renderQueryPeriodId(
          '停机',
          periodDeal?.shutdownTimeTrend,
          periodDeal?.shutdownTime,
          periodDeal?.shutdownTimeRatio,
          periodDeal?.shutdownTimePercent,
          periodDeal?.shutdownTimeCost,
          stateChangeData?.periodData?.shutdownEnergy?.totalValue
        )}
      </div>

      {carousel?.fixedPropertyList?.length ? (
        <div className={`${styles.item} ${styles.carouselContainer}`}>
          <Carousel
            autoplaySpeed={5000}
            arrows={carousel.fixedPropertyList.length > 1}
            autoplay={carousel.fixedPropertyList.length > 1}
            dots={false}
            ref={carouselRef}
            className={styles.carousel}
          >
            {carousel.fixedPropertyList.map((i, index) => (
              <div key={index} className={styles.carouselItem}>
                <p>
                  <span>{i.name}</span>：
                  <span className={styles.value}>
                    {i.value ?? '--'}
                    {i.generalUnitName}
                  </span>
                </p>
              </div>
            ))}
          </Carousel>
        </div>
      ) : null}
    </div>
  );
};

export default TimeCarousel;

const TrendIndicator = (value: number | undefined) => {
  if (isNil(value)) return '--';
  const isUp = value > 0;
  const displayValue = isUp ? value : Math.abs(value);

  return (
    <span className={isUp ? styles.up : styles.down}>
      {keepPercentOrLine(displayValue)}% {isUp ? '↑' : '↓'}
    </span>
  );
};
